import { Select as AntdSelect, type SelectProps, type FormItemProps, Divider, Space, Table } from 'antd';
import { useField } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type UserPreviewDataFragment } from '../../api/fragments/UserPreviewData';
import { type ListAvailableUsersByRoleQuery } from '../../api/queries/listAvailableUsersByRole';
import { type ListAvailableUsersForUserGroupQuery } from '../../api/queries/listAvailableUsersForUserGroup';
import { Select } from './SelectField';
import TableField from './TableField';

type Record = ListAvailableUsersByRoleQuery['users'][number] | ListAvailableUsersForUserGroupQuery['users'][number];

export type SearchUserWithTableFieldProps = Omit<SelectProps, 'options' | 'children'> & {
    name: string;
    users?: UserPreviewDataFragment[];
    fetching?: boolean;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
};

const SearchUserWithTableField = ({
    users: usersFromProps,
    name,
    loading,
    itemProps,
    ...props
}: SearchUserWithTableFieldProps) => {
    const { t } = useTranslation(['common']);

    const [{ value: selectedUserIds }, , { setValue }] = useField({ name });

    const { selectedUsers, userOptions } = useMemo(
        () => ({
            selectedUsers: usersFromProps.filter(user => selectedUserIds.includes(user.id)),
            userOptions: usersFromProps.filter(user => !selectedUserIds.includes(user.id)),
        }),
        [selectedUserIds, usersFromProps]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Select
                loading={loading}
                onChange={newValue => setValue(newValue)}
                placeholder={t('common:searchUserDrawer.placeholder')}
                {...props}
            >
                {userOptions.map(user => (
                    <AntdSelect.Option key={user.id} value={user.id}>
                        {user.displayName}
                    </AntdSelect.Option>
                ))}
            </Select>
            <Divider orientation="left" orientationMargin="0">
                {t('common:searchUserDrawer.usersSelected', { returnObjects: true })}
            </Divider>
            <TableField
                dataSource={selectedUsers}
                loading={loading && !selectedUserIds.length}
                name="users"
                rowKey="id"
                required
            >
                <Table.Column<Record>
                    dataIndex="displayName"
                    onFilter={(value: string, record) => new RegExp(value, 'i').test(record.displayName)}
                    sorter={(a, b) => a.displayName.localeCompare(b.displayName)}
                    title={t('common:searchUserDrawer.displayName')}
                    filterSearch
                />
                <Table.Column<Record>
                    dataIndex="email"
                    onFilter={(value: string, record) => new RegExp(value, 'i').test(record.email)}
                    sorter={(a, b) => a.email.localeCompare(b.email)}
                    title={t('common:searchUserDrawer.email')}
                    filterSearch
                />
            </TableField>
        </Space>
    );
};

export default SearchUserWithTableField;
